import * as React from 'react';

import { KB_HIGHLIGHT_END, KB_HIGHLIGHT_START } from '@/api/kb-client';

// =============================================================================
// kb-snippet.tsx — Rendu SÉCURISÉ du surlignage FTS (R15-COM-04).
// =============================================================================
// Le `snippet` renvoyé par l'API est le TEXTE d'un message (CONTENU ARBITRAIRE, potentiellement
// `<script>…</script>`) où les correspondances de recherche sont encadrées par DEUX sentinelles Unicode
// Private Use Area : KB_HIGHLIGHT_START (U+E000) et KB_HIGHLIGHT_END (U+E001). Ces code points n'existent
// JAMAIS dans un contenu légitime : le découpage est donc déterministe.
//
// SÉCURITÉ (XSS) — RÈGLE ABSOLUE : JAMAIS `dangerouslySetInnerHTML`. On PARSE le snippet en segments
// {text, highlight} puis on rend chaque segment en ÉLÉMENT REACT (`<mark>` si surligné, texte nu sinon) ;
// React ÉCHAPPE le texte, donc aucun HTML n'est jamais interprété (un `<script>` s'affiche comme TEXTE).

/** Un segment de snippet : le texte brut + s'il doit être mis en exergue (`<mark>`). */
export interface KbSnippetSegment {
  readonly text: string;
  readonly highlight: boolean;
}

/**
 * Découpe un snippet sur les sentinelles U+E000 / U+E001 en segments {text, highlight}. Robuste aux
 * sentinelles orphelines/imbriquées : un état booléen bascule à chaque sentinelle rencontrée, et seul le
 * TEXTE (jamais les sentinelles) est conservé. Les segments vides sont omis (rendu compact).
 */
export function parseKbSnippet(snippet: string): KbSnippetSegment[] {
  const segments: KbSnippetSegment[] = [];
  let buffer = '';
  let highlight = false;

  const flush = (): void => {
    if (buffer.length > 0) {
      segments.push({ text: buffer, highlight });
    }
    buffer = '';
  };

  for (const char of snippet) {
    if (char === KB_HIGHLIGHT_START) {
      flush();
      highlight = true;
    } else if (char === KB_HIGHLIGHT_END) {
      flush();
      highlight = false;
    } else {
      buffer += char;
    }
  }
  flush();
  return segments;
}

export interface KbSnippetProps {
  /** Snippet surligné (sentinelles PUA), ou `null` si aucune recherche. */
  readonly snippet: string | null;
  readonly className?: string;
}

/**
 * Rend un snippet FTS : segments normaux en texte ÉCHAPPÉ, segments surlignés dans un `<mark>`. Aucun
 * HTML brut, aucun `dangerouslySetInnerHTML`. `null` → rien.
 */
export function KbSnippet({
  snippet,
  className,
}: KbSnippetProps): React.JSX.Element | null {
  if (snippet === null) {
    return null;
  }
  // Clé stable NON basée sur l'index de tableau : offset cumulé du segment dans le snippet (déterministe,
  // unique, insensible au réordonnancement — les segments ne bougent pas mais on évite l'anti-pattern).
  let offset = 0;
  const keyed = parseKbSnippet(snippet).map((segment) => {
    const key = `${String(offset)}:${segment.highlight ? 'h' : 't'}`;
    offset += segment.text.length + 1;
    return { key, segment };
  });
  return (
    <span className={className}>
      {keyed.map(({ key, segment }) =>
        segment.highlight ? (
          <mark
            key={key}
            className="rounded-sm bg-status-invited-bg px-0.5 text-foreground"
          >
            {segment.text}
          </mark>
        ) : (
          <React.Fragment key={key}>{segment.text}</React.Fragment>
        ),
      )}
    </span>
  );
}
